import React from "react";

// src/config/theme.config.ts
export interface ThemeConfig {
  brand: {
    name: string;
    logo: string;
    favicon: string;
    description: string;
  };
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: {
      primary: string;
      secondary: string;
    };
    status: {
      success: string;
      warning: string;
      error: string;
      info: string;
    };
  };
  typography: {
    fontFamily: string;
    fontSize: {
      xs: string;
      sm: string;
      base: string;
      lg: string;
      xl: string;
      '2xl': string;
      '3xl': string;
    };
  };
}

// Configuración base que puedes personalizar por cliente/tenant
export const defaultTheme: ThemeConfig = {
  brand: {
    name: process.env.REACT_APP_BRAND_NAME || "elPaket",
    logo: "/logo.svg",
    favicon: "/favicon.ico",
    description: "La forma más fácil y rápida de enviar a Venezuela"
  },
  colors: {
    primary: "#2563eb",        // Azul principal
    secondary: "#10b981",      // Verde
    accent: "#ef4444",         // Rojo para CTAs
    background: "#f9fafb",     // Gris muy claro
    surface: "#ffffff",        // Blanco
    text: {
      primary: "#1f2937",      // Gris oscuro
      secondary: "#6b7280"     // Gris medio
    },
    status: {
      success: "#10b981",      // Verde
      warning: "#f59e0b",      // Amarillo
      error: "#ef4444",        // Rojo
      info: "#3b82f6"          // Azul
    }
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    fontSize: {
      xs: "0.75rem",
      sm: "0.875rem",
      base: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      '2xl': "1.5rem",
      '3xl': "1.875rem"
    }
  }
};

// Ejemplo de configuración alternativa para otro cliente
export const alternativeTheme: ThemeConfig = {
  brand: {
    name: "MiaCourier",
    logo: "/mia-logo.svg",
    favicon: "/mia-favicon.ico",
    description: "Conectando familias con envíos seguros"
  },
  colors: {
    primary: "#8b5cf6",        // Morado
    secondary: "#06b6d4",      // Cyan
    accent: "#ec4899",         // Rosa
    background: "#fafafa",
    surface: "#ffffff",
    text: {
      primary: "#0f172a",
      secondary: "#64748b"
    },
    status: {
      success: "#22c55e",
      warning: "#eab308",
      error: "#ef4444",
      info: "#3b82f6"
    }
  },
  typography: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: {
      xs: "0.75rem",
      sm: "0.875rem",
      base: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      '2xl': "1.5rem",
      '3xl': "1.875rem"
    }
  }
};

// Función para obtener el tema activo basado en variables de entorno
export const getActiveTheme = (): ThemeConfig => {
  const themeMode = process.env.REACT_APP_THEME_MODE || 'default';
  
  switch (themeMode) {
    case 'alternative':
      return alternativeTheme;
    default:
      return defaultTheme;
  }
};

// Función para aplicar el tema como CSS variables
export const applyCSSVariables = (theme: ThemeConfig) => {
  const root = document.documentElement;
  
  // Aplicar colores como CSS custom properties
  root.style.setProperty('--color-primary', theme.colors.primary);
  root.style.setProperty('--color-secondary', theme.colors.secondary);
  root.style.setProperty('--color-accent', theme.colors.accent);
  root.style.setProperty('--color-background', theme.colors.background);
  root.style.setProperty('--color-surface', theme.colors.surface);
  root.style.setProperty('--color-text-primary', theme.colors.text.primary);
  root.style.setProperty('--color-text-secondary', theme.colors.text.secondary);
  root.style.setProperty('--color-success', theme.colors.status.success);
  root.style.setProperty('--color-warning', theme.colors.status.warning);
  root.style.setProperty('--color-error', theme.colors.status.error);
  root.style.setProperty('--color-info', theme.colors.status.info);
  
  // Aplicar tipografía
  root.style.setProperty('--font-family', theme.typography.fontFamily);
  root.style.setProperty('--font-size-xs', theme.typography.fontSize.xs);
  root.style.setProperty('--font-size-sm', theme.typography.fontSize.sm);
  root.style.setProperty('--font-size-base', theme.typography.fontSize.base);
  root.style.setProperty('--font-size-lg', theme.typography.fontSize.lg);
  root.style.setProperty('--font-size-xl', theme.typography.fontSize.xl);
  root.style.setProperty('--font-size-2xl', theme.typography.fontSize['2xl']);
  root.style.setProperty('--font-size-3xl', theme.typography.fontSize['3xl']);
};

// Hook personalizado para usar el tema
export const useTheme = () => {
  const theme = getActiveTheme();
  
  React.useEffect(() => {
    applyCSSVariables(theme);
  }, [theme]);
  
  return theme;
};